// Show document <-> scene array. A scene array entry is either an element or [element, opts]
// (the shape JustFuckingDance.js has always used); the document groups entries into blocks.
import React from "react";
import {REGISTRY, nameOf} from "./registry";

// Values that come from the logged-in user rather than the document, e.g. <Ending name={user.name}/>.
export const BIND_SENTINELS = {'user.name': '\u0000bind:user.name'};

const encodeValue = (v) => {
    if (React.isValidElement(v)) return encodeElement(v);
    if (Array.isArray(v)) return v.map(encodeValue);
    if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, encodeValue(x)]));
    if (typeof v === 'function') throw new Error('show documents cannot hold functions');
    const bound = Object.entries(BIND_SENTINELS).find(([, s]) => s === v);
    return bound ? {$bind: bound[0]} : v;
};

export const encodeElement = (el) => {
    const name = typeof el.type === 'string' ? el.type : nameOf(el.type);
    if (!name) throw new Error(`component not in show registry: ${el.type && (el.type.displayName || el.type.name)}`);
    const props = encodeValue(el.props);
    return Object.keys(props).length ? {$el: name, props} : {$el: name};
};

export const encodeEntry = (entry) => {
    if (!Array.isArray(entry)) return {el: encodeElement(entry)};
    const [el, opts] = entry;
    return {el: encodeElement(el), opts: encodeValue(opts)};
};

const decodeValue = (v, ctx) => {
    if (Array.isArray(v)) return v.map(x => decodeValue(x, ctx));
    if (v && typeof v === 'object') {
        if (v.$el) return decodeElement(v, ctx);
        if (v.$bind) return v.$bind.split('.').reduce((o, k) => (o == null ? o : o[k]), ctx);
        return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, decodeValue(x, ctx)]));
    }
    return v;
};

export const decodeElement = ({$el, props}, ctx) => {
    const type = REGISTRY[$el] || $el;
    const {children, ...rest} = props ? decodeValue(props, ctx) : {};
    return children === undefined
        ? React.createElement(type, rest)
        : React.createElement(type, rest, ...(Array.isArray(children) ? children : [children]));
};

export const decodeEntry = (scene, ctx) => {
    const el = decodeElement(scene.el, ctx);
    return scene.opts === undefined ? el : [el, decodeValue(scene.opts, ctx)];
};

// Flat scene array, in show order, for the current client.
export const buildScenes = (doc, ctx) => doc.blocks.flatMap(b => b.scenes.map(s => decodeEntry(s, ctx)));

// Group a flat entry list into blocks: a new block starts at every named scene (the names Ableton's cue
// clips match), unnamed scenes belong to the block before them.
export const blocksFromEntries = (entries) => {
    const blocks = [];
    const seen = {};
    for (const entry of entries) {
        const scene = encodeEntry(entry);
        const name = scene.opts && scene.opts.name;
        if (name || !blocks.length) {
            const base = (name || 'start').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            seen[base] = (seen[base] || 0) + 1;
            blocks.push({id: seen[base] > 1 ? `${base}-${seen[base]}` : base, label: name || 'start', scenes: []});
        }
        blocks[blocks.length - 1].scenes.push(scene);
    }
    return blocks;
};
